import _styled, { default as _styled2, default as _styled3, default as _styled4, default as _styled5, default as _styled6, default as _styled7, default as _styled8, default as _styled9, default as _styled10 } from "styled-components";
import React from "react";
import { m } from "framer-motion";
import { Wrap } from "~/features/ui/wrap";
import { type Mode, type Theme } from "~/features/ui/get-theme-style";
import { Grid } from "~/features/ui/grid";
import { type Shape, Slider } from "~/features/ui/slider";
import { GalleryControls } from "~/features/blocks/inventory/gallery/gallery-controls";
import { up } from "~/features/app/screens";
import { useMediaQuery } from "~/features/ui/use-media-query";
import { padNumber, takeFirst } from "~/features/app/utils";
import { GalleryInfo } from "~/features/blocks/inventory/gallery/gallery-info";
import { GalleryBlockContext } from "~/features/blocks/inventory/gallery/context";
export type GalleryLayout = "boxed" | "full-bleed";
type SectionDataAttrs = Record<`data-${string}`, string | undefined>;
export type GalleryBlockControlState = {
  count: string;
  isActive: boolean;
  isFilled: boolean;
  isLast: boolean;
  mode: Mode;
  onClick: () => void;
  sliderInstance: React.RefObject<Shape | null>;
};
export type GalleryBlockViewProps<TSlide = unknown> = {
  anchor?: Array<{
    id?: string | null;
    name?: string | null;
  }> | {
    id?: string | null;
    name?: string | null;
  } | null;
  eyebrow?: string;
  getSlideKey?: (slide: TSlide, index: number) => React.Key;
  hasInsetDividers?: boolean | null;
  hasTopDivider?: boolean | null;
  headline?: string;
  isHighlighted?: boolean | null;
  layout?: string | null;
  mode?: string | null;
  renderControl: (slide: TSlide, index: number, state: GalleryBlockControlState) => React.ReactNode;
  renderSlide: (slide: TSlide, index: number) => React.ReactNode;
  sectionProps?: React.ComponentPropsWithoutRef<"section"> & SectionDataAttrs;
  slides?: TSlide[] | null;
  text?: React.ReactNode;
  theme?: string | null;
};
export function GalleryBlockView<TSlide = unknown>({
  anchor,
  eyebrow,
  getSlideKey,
  hasInsetDividers = true,
  hasTopDivider = true,
  headline,
  isHighlighted = false,
  layout = "boxed",
  mode = "light",
  renderControl,
  renderSlide,
  sectionProps,
  slides = [],
  text,
  theme = "white"
}: GalleryBlockViewProps<TSlide>) {
  const isLg = useMediaQuery(up("lg"));
  const sliderInstanceRef = React.useRef<Shape>(null);
  const [activeSlideIndex, setActiveSlideIndex] = React.useState(0);
  const [autoPlayStatus, setAutoPlayStatus] = React.useState<"playing" | "paused">("paused");
  React.useEffect(() => {
    const autoplay = sliderInstanceRef.current?.splide?.Components.Autoplay;
    if (!autoplay) {
      return;
    }
    if (autoPlayStatus === "playing") {
      autoplay.play();
    } else {
      autoplay.pause();
    }
  }, [autoPlayStatus]);
  const items = slides ?? [];
  if (items.length === 0) {
    return null;
  }
  const controlsMode = isLg && layout === "full-bleed" ? "dark" : mode as Mode;
  const Controls = <GalleryControls mode={controlsMode}>
      {items.map((slide, idx) => <React.Fragment key={getSlideKey?.(slide, idx) ?? idx}>
          {renderControl(slide, idx, {
        count: padNumber(idx + 1),
        isActive: activeSlideIndex === idx,
        isFilled: autoPlayStatus === "paused",
        isLast: idx === items.length - 1,
        mode: controlsMode,
        onClick: () => sliderInstanceRef?.current?.go(idx),
        sliderInstance: sliderInstanceRef
      })}
        </React.Fragment>)}
    </GalleryControls>;
  return <_StyledMSection {...sectionProps as React.ComponentProps<typeof m.section>} data-theme={mode as Mode} id={takeFirst(anchor)?.id || undefined} data-anchor-name={takeFirst(anchor)?.name || undefined} onClick={() => setAutoPlayStatus("paused")} role="presentation" onViewportEnter={() => setAutoPlayStatus("playing")} onViewportLeave={() => setAutoPlayStatus("paused")} $_css={[{
    "position": "relative",
    "backgroundColor": "var(--bg-secondary)"
  }, hasTopDivider && {
    "borderTopWidth": "1px",
    "borderColor": "var(--stroke-primary)"
  }, isHighlighted && {
    "backgroundColor": "var(--white-static)"
  }]}>
      <_StyledWrap>
        <GalleryBlockContext.Provider value={{
        theme: theme as Theme,
        mode: mode as Mode,
        layout: layout as GalleryLayout,
        hasInsetDividers: hasInsetDividers ?? true
      }}>
          <_StyledGrid $_css2={[{
          "position": "relative",
          "zIndex": "10",
          "@media (min-width: 1024px)": {
            "minHeight": "688px"
          }
        }, layout === "full-bleed" && {
          "@media (min-width: 1024px)": {
            "height": "80rem"
          }
        }]}>
            <_StyledDiv $_css3={[{
            "gridColumn": "1 / -1"
          }, {
            "@media (min-width: 1024px)": {
              "gridColumn": "span 4 / span 4",
              "display": "flex",
              "flexDirection": "column"
            }
          }, hasInsetDividers && {
            "borderBottomWidth": "1px",
            "borderRightWidth": "1px",
            "borderColor": "var(--stroke-primary)",
            "@media (min-width: 1024px)": {
              "borderBottomWidth": "0px"
            }
          }, layout === "full-bleed" && {
            "@media (min-width: 1024px)": {
              "display": "none"
            }
          }]}>
              <GalleryInfo headline={headline} eyebrow={eyebrow} text={text} />
              <_StyledDiv2>{Controls}</_StyledDiv2>
            </_StyledDiv>

            <_StyledDiv3 $_css4={[{
            "gridColumn": "1 / -1"
          }, layout === "boxed" && {
            "@media (min-width: 1024px)": {
              "gridColumn": "span 8 / span 8"
            }
          }]}>
              <_StyledSlider ref={sliderInstanceRef} options={{
              type: "fade",
              autoplay: "pause",
              interval: 5000,
              rewind: true,
              drag: false,
              resetProgress: false,
              pauseOnFocus: false,
              pauseOnHover: false
            }} onMove={splide => setActiveSlideIndex(splide.Components.Controller.getIndex())} hasTrack={false}>
                <_StyledSliderTrack>
                  {items.map((slide, idx) => <_StyledSliderSlide key={getSlideKey?.(slide, idx) ?? idx}>
                      {renderSlide(slide, idx)}
                    </_StyledSliderSlide>)}
                </_StyledSliderTrack>
              </_StyledSlider>
            </_StyledDiv3>

            <_StyledDiv4 $_css5={[{
            "position": "absolute",
            "left": "0",
            "right": "0",
            "bottom": "0",
            "display": "none"
          }, layout === "full-bleed" && {
            "@media (min-width: 1024px)": {
              "display": "block",
              "padding": "4rem"
            }
          }]}>
              {Controls}
            </_StyledDiv4>
          </_StyledGrid>
        </GalleryBlockContext.Provider>
      </_StyledWrap>
    </_StyledMSection>;
}
var _StyledMSection = /*#__PURE__*/_styled(m.section).withConfig({
  componentId: "sc-sf-1uo7g0u-0"
})`${p => p.$_css}`;
var _StyledWrap = _styled2(Wrap).withConfig({
  componentId: "sc-sf-1uo7g0u-1"
})({
  "borderWidth": "0px"
});
var _StyledGrid = _styled3(Grid).withConfig({
  componentId: "sc-sf-1uo7g0u-2"
})`${p => p.$_css2}`;
var _StyledDiv = _styled4("div").withConfig({
  componentId: "sc-sf-1uo7g0u-3"
})`${p => p.$_css3}`;
var _StyledDiv2 = _styled5("div").withConfig({
  componentId: "sc-sf-1uo7g0u-4"
})({
  "marginTop": "auto"
});
var _StyledDiv3 = _styled6("div").withConfig({
  componentId: "sc-sf-1uo7g0u-5"
})`${p => p.$_css4}`;
var _StyledSlider = _styled7(Slider).withConfig({
  componentId: "sc-sf-1uo7g0u-6"
})({
  "height": "100%"
});
var _StyledSliderTrack = _styled8(Slider.Track).withConfig({
  componentId: "sc-sf-1uo7g0u-7"
})({
  "height": "100%"
});
var _StyledSliderSlide = _styled9(Slider.Slide).withConfig({
  componentId: "sc-sf-1uo7g0u-8"
})({
  "height": "100%"
});
var _StyledDiv4 = /*#__PURE__*/_styled10("div").withConfig({
  displayName: "gallery___StyledDiv4",
  componentId: "sc-l3hpz2-0"
})(["", ""], p => p.$_css5);